import { Button, Separator, VText } from '@vake/ui-kit';
import { FC, useEffect, useMemo, useState } from 'react';
import { useClearviewMmsiExample } from '../requests/insights';
import { DrawButton } from '../shared/components/DrawToggler';
import { DetectionImage } from '../shared/components/Popup';
import { InputFieldTW } from '../shared/styles/InputFields';
import { apiBaseUrl } from '../utils/env';
import { SimpleDateSelector } from './Report/interactiveReport/reportInfo/DateSelector';

export const ClearViewMmsiHolder: FC<{
    setFindThisBoat: (value: string[] | null) => void;
    fromDate: Date | null;
    toDate: Date | null;
    drawMode: boolean;
    setFromDate: (value: Date | null) => void;
    setToDate: (value: Date | null) => void;
    setDrawMode: (value: boolean) => void;
    isPolygon: boolean;
    mmsiFromURL: number | null;
    setClearviewErrorMsg: (value: string | null) => void;
    searchUuidFromURL: string[] | null;
}> = ({
    setFindThisBoat,
    fromDate,
    toDate,
    drawMode,
    setFromDate,
    setToDate,
    setDrawMode,
    isPolygon,
    mmsiFromURL,
    setClearviewErrorMsg,
    searchUuidFromURL,
}) => {
    const [mmsiOfInterestToQuery, setMmsiOfInterestToQuery] = useState<number | null>(null);
    const [mmsiOfInterest, setMmsiOfInterest] = useState<number | null>(mmsiFromURL);
    const { data: knownInsights } = useClearviewMmsiExample(mmsiOfInterestToQuery, setClearviewErrorMsg);
    const [chosenImages, setHighlightedImages] = useState<number[] | null>(null);
    const [validImageData, setValidImageData] = useState<boolean>(false);

    useEffect(() => {
        if (searchUuidFromURL) {
            setHighlightedImages([...Array(searchUuidFromURL.length).keys()]);
        }
    }, [searchUuidFromURL]);

    useEffect(() => {
        if (mmsiFromURL) {
            setMmsiOfInterest(mmsiFromURL);
            setMmsiOfInterestToQuery(mmsiFromURL);
        }
    }, [mmsiFromURL]);

    const errorMessage: string | undefined = useMemo(() => {
        if (knownInsights && knownInsights.features) {
            if (knownInsights.features.length > 0) {
                setValidImageData(true);
                return undefined;
            } else {
                setValidImageData(false);
            }
            return 'We have no images of vessel ' + mmsiOfInterestToQuery + ', try another mmsi';
        }
    }, [knownInsights, mmsiOfInterestToQuery]);

    const handleMmsiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const userInput = event.target.value;
        const isInt = Number(userInput);
        if (isInt || userInput === '') {
            setMmsiOfInterest(isInt);
        }
    };
    const handleMmsiSearchClick = () => {
        if (mmsiOfInterest) {
            setMmsiOfInterestToQuery(mmsiOfInterest);
        }
    };

    const HandleSearchVesselClick = (indeces: number[]) => {
        if (knownInsights && knownInsights.features) {
            const uuids: string[] = indeces.map((idx) => {
                const feature = knownInsights.features[idx];
                if (feature && feature.properties && feature.properties.uuid) {
                    return feature.properties.uuid;
                } else return undefined;
            });
            setFindThisBoat(uuids);
        } else if (searchUuidFromURL && searchUuidFromURL.length > 0) {
            setFindThisBoat(searchUuidFromURL);
        }
    };
    const addChosenImage = (index: number) => {
        if (chosenImages === null) {
            setHighlightedImages([index]);
        } else {
            // append if exist
            setHighlightedImages([...chosenImages, index]);
        }
    };
    const HandleRemoveHighlightVessel = (index: number) => {
        if (chosenImages?.includes(index)) {
            // Remove if already clicked
            const indexRemoved = chosenImages.filter((element) => {
                return element !== index;
            });
            setHighlightedImages(indexRemoved);
        }
    };

    useEffect(() => {
        if (fromDate !== null && toDate !== null) {
            if (fromDate > toDate) {
                setFromDate(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, setFromDate]);
    useEffect(() => {
        if (fromDate !== null && toDate !== null) {
            if (fromDate > toDate) {
                setToDate(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toDate, setToDate]);
    return (
        <div className="flex flex-col">
            {!searchUuidFromURL ? (
                <InputFieldTW
                    type="text"
                    onChange={handleMmsiChange}
                    placeholder={mmsiFromURL ? String(mmsiFromURL) : 'Insert MMSI Number'}
                />
            ) : null}
            <div className="flex gap-2">
                <SimpleDateSelector
                    buttonText={fromDate !== null ? fromDate.toDateString().slice(4) : 'Start Date'}
                    date={fromDate}
                    setDate={setFromDate}
                    endOfDate={false}
                />
                <SimpleDateSelector
                    buttonText={toDate !== null ? toDate.toDateString().slice(4) : 'End Date'}
                    date={toDate}
                    setDate={setToDate}
                    endOfDate={true}
                />
            </div>
            <DrawButton
                isFinished={isPolygon}
                onClick={() => setDrawMode(!drawMode)}
                isDrawMode={drawMode}
            ></DrawButton>
            {!searchUuidFromURL ? <Button text="Get Known Images" onClick={handleMmsiSearchClick}></Button> : null}
            {validImageData || searchUuidFromURL ? (
                <>
                    <Separator></Separator>
                    <Button
                        text="Find Matching Images"
                        onClick={() => {
                            if (chosenImages !== null) {
                                HandleSearchVesselClick(chosenImages);
                            }
                        }}
                    ></Button>
                </>
            ) : null}
            {searchUuidFromURL && searchUuidFromURL.length > 0 ? (
                <div className="flex">
                    {searchUuidFromURL.map((item, index) => {
                        return (
                            <DetectionImage
                                key={index}
                                image_path={apiBaseUrl() + '/insights/image/sentinel2/' + item + '.png'}
                            />
                        );
                    })}
                </div>
            ) : null}

            {validImageData && knownInsights ? (
                <div className="flex flex-col itemcs-center h-[480px] overflow-y-scroll gap-1">
                    {knownInsights.features.map((item, index) => {
                        if (!!item.properties && item.properties.uuid) {
                            return (
                                <div key={index} className="relative">
                                    {chosenImages?.includes(index) ? (
                                        <div
                                            className="absolute inset-0 flex justify-center items-center z-10 text-center border-2 border-success-background "
                                            onClick={() => HandleRemoveHighlightVessel(index)}
                                        >
                                            <div className="bg-success-background p-1 rounded">
                                                <VText>chosen vessel</VText>
                                            </div>
                                        </div>
                                    ) : null}
                                    <div
                                        className="flex"
                                        onClick={() => {
                                            addChosenImage(index);
                                        }}
                                    >
                                        <DetectionImage
                                            key={index}
                                            image_path={
                                                apiBaseUrl() +
                                                '/insights/image/sentinel2/' +
                                                item.properties.uuid +
                                                '.png'
                                            }
                                        />
                                    </div>
                                </div>
                            );
                        } else return null;
                    })}
                </div>
            ) : errorMessage ? (
                <div className="text-center bg-danger-background rounded">{errorMessage} </div>
            ) : null}
        </div>
    );
};
